"use client";

import { Button, cn, confirm, toast } from "@devhop/ui";
import { PlusIcon, Trash2Icon } from "lucide-react";
import type { WorkshopJobDetail } from "../api/client";
import {
  useRemoveWorkshopJobLaborMutation,
  useWorkshopQuoteQuery,
} from "../api/queries";
import { formatWorkshopMoney } from "../lib/workshop-labels";
import { workshopToastError } from "../lib/workshop-user-messages";

export function WorkshopJobLaborsSummary({
  shopId,
  job,
  canEdit,
  onAdd,
  className,
}: {
  shopId: string;
  job: WorkshopJobDetail;
  canEdit?: boolean;
  onAdd?: () => void;
  className?: string;
}) {
  const quoteQ = useWorkshopQuoteQuery(shopId, job.id);
  const removeMut = useRemoveWorkshopJobLaborMutation(shopId);
  const labors = job.labors ?? [];
  const subtotal = quoteQ.data?.laborsSubtotal;

  async function removeLabor(laborId: string, name: string) {
    const ok = await confirm({
      title: "ລຶບຄ່າແຮງ",
      description: `ລຶບ «${name}» ອອກຈາກໃບງານ ຫຼື ບໍ່?`,
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await removeMut.mutateAsync({ jobId: job.id, laborId });
      toast.success("ລຶບຄ່າແຮງແລ້ວ");
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  return (
    <div className={cn("space-y-3", className)}>
      {labors.length === 0 ? (
        <p className="py-1 text-muted-foreground text-sm">ຍັງບໍ່ມີຄ່າແຮງ</p>
      ) : (
        <ul>
          {labors.map((l) => (
            <li
              key={l.id}
              className="flex items-start justify-between gap-2 border-border border-b py-2.5 last:border-b-0"
            >
              <div className="min-w-0">
                <p className="font-medium text-sm">{l.name}</p>
                <p className="text-muted-foreground text-xs">× {l.qty}</p>
              </div>
              <div className="flex shrink-0 items-start gap-1">
                <p className="font-semibold text-sm tabular-nums">
                  {formatWorkshopMoney(l.lineTotal, l.currency)}
                </p>
                {canEdit ? (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-8 text-muted-foreground hover:text-destructive"
                    disabled={removeMut.isPending}
                    onClick={() => void removeLabor(l.id, l.name)}
                  >
                    <Trash2Icon className="size-4" />
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {labors.length > 0 ? (
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="text-muted-foreground">ລວມຄ່າແຮງ</span>
          <span className="font-bold tabular-nums">
            {quoteQ.isLoading ? (
              <span className="text-muted-foreground">…</span>
            ) : subtotal != null ? (
              formatWorkshopMoney(subtotal, job.currency)
            ) : (
              <span className="text-amber-800 text-xs">ຕ້ອງຕັ້ງອັດຕາແລກປ່ຽນ</span>
            )}
          </span>
        </div>
      ) : null}

      {canEdit && onAdd ? (
        <Button
          type="button"
          variant="outline"
          className="h-10 w-full"
          onClick={onAdd}
        >
          <PlusIcon className="size-4" />
          ເພີ່ມຄ່າແຮງ
        </Button>
      ) : null}
    </div>
  );
}
